import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Button,
    Space,
    Avatar,
    message,
    Empty,
    Popconfirm,
    Typography,
    Divider,
    Tag,
    theme,
    Card,
    Grid,
} from 'antd';
import {
    SendOutlined,
    DeleteOutlined,
    CommentOutlined,
} from '@ant-design/icons';
import { crmApi } from '../../../api/client';
import RichMentionEditor from '../../../components/common/RichMentionEditor';
import MentionDisplay from '../../../components/common/MentionDisplay';
import { useNotificationRefresh } from '../../../hooks/useNotificationRefresh';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pl';

dayjs.extend(relativeTime);
dayjs.locale('pl');

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface Comment {
    id: string;
    content: string;
    createdAt: string;
    author: { id: string; firstName: string; lastName: string };
    mentionedUserIds?: string[];
}

interface Props {
    contractorId: string;
}

export default function ContractorComments({ contractorId }: Props) {
    const { token } = theme.useToken();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const queryClient = useQueryClient();
    const refreshNotifications = useNotificationRefresh();

    const [commentText, setCommentText] = useState('');

    const { data: comments = [], isLoading } = useQuery<Comment[]>({
        queryKey: ['contractor-comments', contractorId],
        queryFn: () => crmApi.getComments(contractorId).then((r: any) => r.data),
    });

    const createCommentMutation = useMutation({
        mutationFn: (data: any) => crmApi.addComment(contractorId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contractor-comments', contractorId] });
            refreshNotifications();
            setCommentText('');
            message.success('Komentarz dodany');
        },
        onError: () => message.error('Błąd dodawania komentarza'),
    });

    const deleteCommentMutation = useMutation({
        mutationFn: (id: string) => crmApi.deleteComment(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['contractor-comments', contractorId] });
            refreshNotifications();
            message.success('Komentarz usunięty');
        },
        onError: () => message.error('Błąd usuwania'),
    });

    const handleAddComment = () => {
        if (!commentText.trim()) return;
        createCommentMutation.mutate({ content: commentText });
    };

    return (
        <Card
            title={
                <Space>
                    <CommentOutlined />
                    Komentarze
                    <Tag>{comments.length}</Tag>
                </Space>
            }
            style={{ height: '100%' }}
            bodyStyle={{ display: 'flex', flexDirection: 'column', height: '100%', padding: isMobile ? '12px' : 24 }}
        >
            {/* Input */}
            <div style={{ marginBottom: 16 }}>
                <RichMentionEditor
                    value={commentText}
                    onChange={setCommentText}
                    placeholder="Napisz komentarz... (@wzmianka)"
                    minRows={2}
                />
                <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleAddComment}
                    loading={createCommentMutation.isPending}
                    disabled={!commentText.trim()}
                    style={{ marginTop: 8, width: '100%' }}
                >
                    Dodaj komentarz
                </Button>
            </div>

            <Divider />

            {/* List */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {comments.length === 0 ? (
                    <Empty description="Brak komentarzy" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                        {comments.map(comment => (
                            <Card key={comment.id} size="small" style={{ background: token.colorFillQuaternary }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <Space>
                                        <Avatar size="small">{comment.author.firstName[0]}</Avatar>
                                        <Text strong>{comment.author.firstName} {comment.author.lastName}</Text>
                                    </Space>
                                    <Space>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(comment.createdAt).fromNow()}</Text>
                                        <Popconfirm
                                            title="Usunąć komentarz?"
                                            onConfirm={() => deleteCommentMutation.mutate(comment.id)}
                                        >
                                            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                                        </Popconfirm>
                                    </Space>
                                </div>
                                <div style={{ fontSize: 14 }}>
                                    <MentionDisplay content={comment.content} />
                                </div>
                            </Card>
                        ))}
                    </Space>
                )}
            </div>
        </Card>
    );
}
